import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Iconify from "../../components/iconify/Iconify";
import { ITEM_TYPE_COLORS } from "../../constants/enums";
import { formatCurrency } from "../../utils/currency";

function ItemCard({ item, showImages = true, onAddItem, type = "Product" }) {
  const [showFlash, setShowFlash] = useState(false);
  const [tapCount, setTapCount] = useState(0);

  const resetTimerRef = useRef(null);
  const flashTimerRef = useRef(null);
  const TAP_RESET_DELAY = 1500; // 1.5 seconds

  const itemVariants = item?.variants || [];
  const ItemVariant = item?.variants ? item?.variants[0] : {};
  const productTypes = item?.product_type || {};
  const productTypeNames = productTypes?.product_type_name;
  const longText = ItemVariant?.name?.length > 25 ? "menu-item-name-long" : "";
  const discount =
    ItemVariant?.current_price && ItemVariant?.current_price?.original_price
      ? Math.round(
          ((ItemVariant?.current_price?.original_price -
            ItemVariant?.current_price?.final_price) /
            ItemVariant?.current_price?.original_price) *
            100
        )
      : 0;

  const handleAddItem = () => {
    const needsSelection =
      ((item.variants && item.variants.length > 1) ||
        item.addons?.length > 0) &&
      type === "Product";

    if (needsSelection) {
      onAddItem(ItemVariant, item);
      return;
    }

    showFlashOnItem();
    onAddItem(ItemVariant, item);
  };

  const showFlashOnItem = () => {
    // Increase streak
    setTapCount((prev) => prev + 1);

    // Trigger flash (restart animation)
    setShowFlash(false);
    requestAnimationFrame(() => setShowFlash(true));

    // Reset flash visibility
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setShowFlash(false);
    }, 800);

    // Reset streak after inactivity
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setTapCount(0);
    }, TAP_RESET_DELAY);
  };

  useEffect(() => {
    return () => {
      clearTimeout(resetTimerRef.current);
      clearTimeout(flashTimerRef.current);
    };
  }, []);

  return (
    <Box
      className={`menu-item-card-wrap ${
        type === "Variant" ? "menu-item-variant-view" : ""
      }`}
    >
      <ButtonBase
        component="div"
        onClick={handleAddItem}
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <Box className="menu-item-card">
          {/* Discount Tag */}
          {discount !== 0 && (
            <Box className="menu-item-off-wrap">
              <Box className="menu-item-off-img">
                <svg
                  width="29"
                  height="28"
                  viewBox="0 0 29 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M28.9499 0C28.3999 0 27.9361 1.44696 27.9361 2.60412V27.9718L24.5708 25.9718L21.2055 27.9718L17.8402 25.9718L14.4749 27.9718L11.1096 25.9718L7.74436 27.9718L4.37907 25.9718L1.01378 27.9718V2.6037C1.01378 1.44655 0.549931 0 0 0H28.9499Z"
                    fill="#538CEE"
                  />
                </svg>
                <Box className="menu-item-off-text">{discount}%</Box>
              </Box>
            </Box>
          )}

          {/* Show Image if enabled */}
          {showImages && ItemVariant.image && (
            <Box
              sx={{
                width: "100%",
                height: "50%",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={ItemVariant.image}
                alt={ItemVariant?.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}

          {/* Item Name & Price */}
          <Box
            className="menu-item-name"
            sx={{ display: "flex", gap: "5px", alignItems: "center" }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: ITEM_TYPE_COLORS[productTypeNames] || "",
                }}
              />
            </Box>
            <Box>
              <Box sx={{ overflow: "hidden", width: "100%" }}>
                <Typography
                  variant="subtitle2"
                  className={longText}
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: "1.2",
                  }}
                >
                  {item?.product_name}
                </Typography>
                <Box className="menu-cat-items">
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {itemVariants.length > 1 && (
                      <Box
                        style={{
                          fontSize: 11,
                          color: "#666",
                        }}
                      >
                        From
                      </Box>
                    )}
                    <Box>
                      {formatCurrency(ItemVariant?.current_price?.final_price)}
                    </Box>
                    {/* Show this only if item has variants or addons */}
                    {/* Permanent Customizable Badge */}
                    {(item.variants?.length > 1 || item.addons?.length > 0) && (
                      <Tooltip title="Customizable Item">
                        <Box
                          sx={{
                            color: "warning.darker",
                            fontSize: "0.65rem",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Iconify icon="mdi:plus-circle" width={12} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </ButtonBase>
      {showFlash && (
        <Box className="flash-container">
          <Typography className="flash-number">{tapCount}</Typography>
        </Box>
      )}
    </Box>
  );
}

export default ItemCard;
